import React from 'react';
import { FiClock, FiLoader, FiMail, FiRefreshCw, FiSend, FiUser } from 'react-icons/fi';
import * as utils from '../../utils/utils';
import styles from './Messages.module.css';
import Sidebar from '../Sidebar/Sidebar';

export default class Messages extends React.Component {
	constructor(props) {
		super(props);

		this.messageListRef = React.createRef();
		this.activeMessagesRequestThreadId = null;

		this.state = {
			isLoadingThreads: true,
			isLoadingMessages: false,
			isSendingMessage: false,
			errorText: '',
			threads: [],
			activeThreadId: null,
			activeThread: null,
			messages: [],
			messageText: '',
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadThreads();
	}

	componentWillUnmount() {
		this.activeMessagesRequestThreadId = null;
	}

	getCurrentUserID = () => {
		return utils.getCurrentUserID();
	};

	getCurrentUserName = () => {
		const fullName = `${utils.getUserFirstName() || ''} ${utils.getUserLastName() || ''}`.trim();
		return fullName === '' ? 'You' : fullName;
	};

	getCurrentUserInitials = () => {
		const firstName = (utils.getUserFirstName() || '').trim();
		const lastName = (utils.getUserLastName() || '').trim();
		const initials = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
		return initials === '' ? 'ME' : initials;
	};

	getThreadName = (thread) => {
		return `${thread?.targetFirstName || ''} ${thread?.targetLastName || ''}`.trim() || 'Conversation';
	};

	getThreadInitials = (thread) => {
		const firstName = (thread?.targetFirstName || '').trim();
		const lastName = (thread?.targetLastName || '').trim();
		const initials = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
		return initials === '' ? 'DM' : initials;
	};

	formatTimestamp = (value) => {
		if (!value) {
			return '';
		}

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return '';
		}

		return date.toLocaleString([], {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	};

	formatTimeOnly = (value) => {
		if (!value) {
			return '';
		}

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return '';
		}

		return date.toLocaleTimeString([], {
			hour: 'numeric',
			minute: '2-digit',
		});
	};

	truncateText = (value, maxLength = 96) => {
		const text = String(value || '').trim();
		if (text.length <= maxLength) {
			return text;
		}

		return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
	};

	getLastMessagePreview = (thread) => {
		const messageText = String(thread?.lastMessageText || '').trim();

		if (messageText === '') {
			return 'No messages yet';
		}

		const prefix = Number(thread?.lastUserId) === this.getCurrentUserID() ? 'You: ' : '';
		return this.truncateText(`${prefix}${messageText}`, 92);
	};

	sortThreads = (threads) => {
		return [...threads].sort((leftThread, rightThread) => {
			const leftTime = new Date(leftThread?.lastMessageAt || 0).getTime();
			const rightTime = new Date(rightThread?.lastMessageAt || 0).getTime();
			return rightTime - leftTime;
		});
	};

	scrollMessagesToBottom = () => {
		const messageListElement = this.messageListRef.current;
		if (!messageListElement) {
			return;
		}

		messageListElement.scrollTop = messageListElement.scrollHeight;
	};

	loadThreads = async () => {
		this.setState({
			isLoadingThreads: true,
			errorText: '',
		});

		try {
			const response = await utils.sendRequest('/message/listThreads', {
				offset: 0,
				limit: 100,
			});

			const threads = this.sortThreads(Array.isArray(response.threads) ? response.threads : []);
			const selectedThread = threads.length > 0 ? threads[0] : null;

			this.setState({
				isLoadingThreads: false,
				threads,
				errorText: '',
				activeThreadId: selectedThread ? Number(selectedThread.userChatThreadId) : null,
				activeThread: selectedThread,
				messages: [],
			}, () => {
				if (selectedThread) {
					this.loadMessagesForThread(selectedThread);
					return;
				}

				this.scrollMessagesToBottom();
			});
		} catch (error) {
			this.setState({
				isLoadingThreads: false,
				errorText: error && error.message ? error.message : 'Could not load conversations.',
				threads: [],
				activeThreadId: null,
				activeThread: null,
				messages: [],
			});
		}
	};

	selectThread = (thread) => {
		if (!thread) {
			return;
		}

		const threadId = Number(thread.userChatThreadId);
		if (Number.isFinite(threadId) && Number(this.state.activeThreadId) === threadId) {
			return;
		}

		this.setState({
			activeThreadId: threadId,
			activeThread: thread,
			messages: [],
			errorText: '',
		}, () => {
			this.loadMessagesForThread(thread);
		});
	};

	loadMessagesForThread = async (thread) => {
		const threadId = Number(thread?.userChatThreadId);
		if (!Number.isInteger(threadId) || threadId <= 0) {
			return;
		}

		this.activeMessagesRequestThreadId = threadId;

		this.setState({
			isLoadingMessages: true,
			errorText: '',
		});

		try {
			const response = await utils.sendRequest('/message/listMessages', {
				userChatThreadID: threadId,
				limit: 100,
			});

			if (this.activeMessagesRequestThreadId !== threadId) {
				return;
			}

			this.setState({
				isLoadingMessages: false,
				messages: Array.isArray(response.messages) ? response.messages : [],
				activeThread: thread,
				errorText: '',
			}, this.scrollMessagesToBottom);
		} catch (error) {
			if (this.activeMessagesRequestThreadId !== threadId) {
				return;
			}

			this.setState({
				isLoadingMessages: false,
				errorText: error && error.message ? error.message : 'Could not load messages.',
			});
		}
	};

	onMessageTextChange = (event) => {
		this.setState({
			messageText: event.target.value,
		});
	};

	onMessageTextKeyDown = (event) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.sendMessage();
		}
	};

	sendMessage = async () => {
		const {
			activeThread,
			isSendingMessage,
			messageText,
		} = this.state;

		if (isSendingMessage || !activeThread) {
			return;
		}

		const text = messageText.trim();
		if (text === '') {
			return;
		}

		const threadId = Number(activeThread.userChatThreadId);

		this.setState({
			isSendingMessage: true,
			errorText: '',
		});

		try {
			const response = await utils.sendRequest('/message/send', {
				userChatThreadID: threadId,
				messageText: text,
			});

			const sentMessage = response.message;
			if (!sentMessage) {
				throw new Error('Message was not saved.');
			}

			this.setState((prevState) => {
				const updatedThread = {
					...prevState.activeThread,
					lastMessageAt: sentMessage.createdAt,
					lastMessageText: sentMessage.messageText,
					lastUserId: sentMessage.senderUserId,
				};

				const updatedThreads = this.sortThreads(
					prevState.threads.map((thread) => {
						if (Number(thread.userChatThreadId) !== threadId) {
							return thread;
						}

						return {
							...thread,
							lastMessageAt: sentMessage.createdAt,
							lastMessageText: sentMessage.messageText,
							lastUserId: sentMessage.senderUserId,
						};
					})
				);

				return {
					isSendingMessage: false,
					messageText: '',
					messages: [...prevState.messages, sentMessage],
					activeThread: updatedThread,
					threads: updatedThreads,
				};
			}, this.scrollMessagesToBottom);
		} catch (error) {
			this.setState({
				isSendingMessage: false,
				errorText: error && error.message ? error.message : 'Could not send message.',
			});
		}
	};

	renderThreadItem = (thread) => {
		const threadId = Number(thread.userChatThreadId);
		const isActive = Number(this.state.activeThreadId) === threadId;
		const threadName = this.getThreadName(thread);

		return (
			<button
				key={threadId}
				type="button"
				className={`${styles.threadItem} ${isActive ? styles.threadItemActive : ''}`}
				onClick={() => this.selectThread(thread)}
			>
				<div className={styles.threadAvatar}>{this.getThreadInitials(thread)}</div>

				<div className={styles.threadBody}>
					<div className={styles.threadHeaderRow}>
						<h3 className={styles.threadName}>{threadName}</h3>
						<span className={styles.threadTime}>{this.formatTimeOnly(thread.lastMessageAt)}</span>
					</div>

					<p className={styles.threadPreview}>{this.getLastMessagePreview(thread)}</p>
				</div>
			</button>
		);
	};

	renderMessageBubble = (message) => {
		const myUserID = this.getCurrentUserID();
		const isMine = Number(message.senderUserId) === myUserID;

		return (
			<div
				key={message.userChatMessageId}
				className={`${styles.messageRow} ${isMine ? styles.messageRowMine : styles.messageRowTheirs}`}
			>
				<div className={`${styles.messageBubble} ${isMine ? styles.messageBubbleMine : styles.messageBubbleTheirs}`}>
					<p className={styles.messageText}>{message.messageText}</p>
					<div className={styles.messageMeta}>
						<FiClock />
						<span>{this.formatTimestamp(message.createdAt)}</span>
					</div>
				</div>
			</div>
		);
	};

	render() {
		const {
			isLoadingThreads,
			isLoadingMessages,
			isSendingMessage,
			errorText,
			threads,
			activeThread,
			messages,
			messageText,
		} = this.state;

		const activeThreadName = activeThread ? this.getThreadName(activeThread) : 'Select a conversation';
		const activeThreadSubtitle = activeThread
			? `Direct message thread with ${activeThreadName}`
			: 'Your conversation list appears here.';

		return (
			<div className={styles.pageContainer}>
                <Sidebar />

				<div className={styles.glowLayer}>
					<div className={styles.glowTop} />
					<div className={styles.glowBottom} />
				</div>

				<div className={styles.layoutShell}>
					<aside className={styles.leftPanel}>
						{/* <div className={styles.brandBadge}>LinkedUp</div> */}

						<div className={styles.profileBlock}>
							<div className={styles.avatarCircle}>{this.getCurrentUserInitials()}</div>
							<div>
								<h1 className={styles.title}>Messages</h1>
								<p className={styles.subtitle}>{this.getCurrentUserName()}</p>
							</div>
						</div>

						<div className={styles.metricsGrid}>
							<div className={styles.metricCard}>
								<span className={styles.metricLabel}>Conversations</span>
								<span className={styles.metricValue}>{threads.length}</span>
							</div>
							<div className={styles.metricCard}>
								<span className={styles.metricLabel}>Active Thread</span>
								<span className={styles.metricValue}>{activeThread ? this.getThreadName(activeThread) : 'None'}</span>
							</div>
						</div>

						<div className={styles.sectionHeader}>
							<h2 className={styles.sectionTitle}>Recent Threads</h2>
							<p className={styles.formText}>
								Choose a thread to load the latest conversation history.
							</p>
						</div>

						<div className={styles.threadListWrap}>
							{
								isLoadingThreads &&
								<div className={styles.threadStatusBox}>
									<FiLoader className={styles.spinIcon} />
									Loading conversations...
								</div>
							}

							{
								!isLoadingThreads && errorText !== '' &&
								<div className={styles.threadErrorBox}>{errorText}</div>
							}

							{
								!isLoadingThreads && errorText === '' && threads.length === 0 &&
								<div className={styles.threadEmptyBox}>
									<FiMail className={styles.emptyIcon} />
									<strong>No conversations yet.</strong>
									<span>New matches and messages will appear here.</span>
								</div>
							}

							{
								!isLoadingThreads && errorText === '' && threads.length > 0 &&
								<div className={styles.threadList}>
									{threads.map(this.renderThreadItem)}
								</div>
							}
						</div>
					</aside>

					<section className={styles.cardPanel}>
						<div className={styles.sectionHeaderRow}>
							<div className={styles.sectionHeadingGroup}>
								<p className={styles.sectionEyebrow}>Direct chat</p>
								<h2 className={styles.formTitle}>{activeThreadName}</h2>
								<p className={styles.formText}>{activeThreadSubtitle}</p>
							</div>

							<button
								type="button"
								className={styles.refreshButton}
								onClick={this.loadThreads}
								disabled={isLoadingThreads}
							>
								<FiRefreshCw />
								Refresh
							</button>
						</div>

						<div className={styles.chatShell}>
							<div className={styles.messageList} ref={this.messageListRef}>
								{
									isLoadingMessages &&
									<div className={styles.chatStatusBox}>
										<FiLoader className={styles.spinIcon} />
										Loading messages...
									</div>
								}

								{
									!isLoadingMessages && !activeThread &&
									<div className={styles.chatEmptyBox}>
										<FiUser className={styles.emptyIcon} />
										<strong>Select a conversation.</strong>
										<span>The chat pane updates once you pick a thread on the left.</span>
									</div>
								}

								{
									!isLoadingMessages && activeThread && messages.length === 0 &&
									<div className={styles.chatEmptyBox}>
										<FiMail className={styles.emptyIcon} />
										<strong>No messages yet.</strong>
										<span>Send the first message to open this conversation.</span>
									</div>
								}

								{
									!isLoadingMessages && messages.length > 0 &&
									<div className={styles.messageStack}>
										{messages.map(this.renderMessageBubble)}
									</div>
								}
							</div>

							<div className={styles.composerShell}>
								<textarea
									className={styles.composeInput}
									value={messageText}
									onChange={this.onMessageTextChange}
									onKeyDown={this.onMessageTextKeyDown}
									placeholder={activeThread ? `Message ${activeThreadName}...` : 'Choose a conversation first'}
									rows={3}
									disabled={!activeThread || isSendingMessage}
								/>

								<div className={styles.composerFooter}>
									<p className={styles.composeHint}>Enter to send, Shift+Enter for a new line.</p>

									<button
										type="button"
										className={styles.sendButton}
										onClick={this.sendMessage}
										disabled={!activeThread || messageText.trim() === '' || isSendingMessage}
									>
										<FiSend />
										{isSendingMessage ? 'Sending...' : 'Send'}
									</button>
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
		);
	}
}
